import { addError, addWarning, logger } from "@wayflyer/blackbox-fe";

/**
 * Groups a refresh failure by failure mode rather than by message or stack. Neither is stable: the
 * minified stack changes every release, and some Auth0 messages embed a wall-clock time and a
 * localized timezone, so grouping on either splits one problem across many error tracking issues.
 */
export function failureFingerprint(label: string, error: unknown): string[] {
  const cause = error as { error?: unknown; message?: unknown; name?: unknown } | null | undefined;
  if (typeof cause?.error === "string" && cause.error) {
    return [label, cause.error];
  }
  if (typeof cause?.message === "string" && cause.message) {
    // Auth0 puts the volatile wall-clock time and timezone in parentheses. Stripping digits instead
    // would also rewrite identifiers, turning "<Auth0Provider>" into "<AuthProvider>".
    // Not truncated: two long messages sharing a prefix would otherwise collapse into one issue.
    return [
      label,
      cause.message
        .replace(/\([^)]*\)/g, "")
        .replace(/[\s)]+/g, " ")
        .trim(),
    ];
  }
  return [label, typeof cause?.name === "string" && cause.name ? cause.name : "unknown"];
}

/**
 * How much attention a failure mode deserves.
 *
 * - `info`: the session is over and Auth0 wants the user to authenticate again.
 * - `warning`: the refresh did not complete, and the next attempt may succeed.
 * - `error`: unexpected. Something is wrong with our code or our Auth0 configuration.
 */
export type FailureLevel = "info" | "warning" | "error";

/**
 * Auth0 refuses a silent refresh because the user has to interact: the SSO session has ended, or
 * Auth0 wants MFA or a new password. No retry can satisfy any of these, and none is a defect.
 */
const EXPECTED_CODES = new Set([
  "consent_required",
  "interaction_required",
  "login_required",
  "mfa_required",
  "password_change_required",
]);

/** The code auth0-spa-js gives its own `TimeoutError`, from silent auth and from a lock it cannot take. */
const TRANSIENT_CODES = new Set(["timeout"]);

/**
 * Messages that have to classify themselves, because the cause carries no Auth0 error code.
 *
 * The first is auth0-spa-js racing the token `fetch` against a deadline. It rejects with a plain
 * `Error`, not the `TimeoutError` that would carry `timeout`, so nothing but the message identifies
 * it. The rest are what each browser gives a `fetch` that never reached a response.
 *
 * No `instanceof` test guards this: the deadline is an `Error` and the browser rejections are
 * `TypeError`s, so no single constructor covers the set, and each message is specific enough alone.
 */
const TRANSIENT_MESSAGES = new Set([
  "Timeout when executing 'fetch'", // auth0-spa-js, racing fetch against DEFAULT_FETCH_TIMEOUT_MS
  "Failed to fetch", // Chrome
  "Load failed", // Safari
  "Network request failed", // polyfill from https://github.com/github/fetch
  "NetworkError when attempting to fetch resource.", // Firefox
  "The network connection was lost.", // Safari
]);

export function classifyFailure(error: unknown): FailureLevel {
  const cause = error as { error?: unknown; message?: unknown; name?: unknown } | null | undefined;
  const code = typeof cause?.error === "string" ? cause.error : undefined;

  if (code !== undefined && EXPECTED_CODES.has(code)) {
    return "info";
  }
  if (code !== undefined && TRANSIENT_CODES.has(code)) {
    return "warning";
  }
  if (typeof cause?.message === "string" && TRANSIENT_MESSAGES.has(cause.message)) {
    return "warning";
  }
  return "error";
}

/**
 * Failure modes already reported since the last successful refresh.
 *
 * A session that cannot be refreshed retries every 30 seconds for as long as the tab is open, and
 * each attempt reports again. The user reaches the login page either way, so the number of attempts
 * is not information anyone acts on - one report per failure mode per episode is.
 */
const reportedModes = new Set<string>();

/** Lets the next failure report again. Call when a refresh succeeds. */
export function clearReportedFailures(): void {
  reportedModes.clear();
}

export function reportFailure(label: string, error: unknown): void {
  const fingerprint = failureFingerprint(label, error);
  const mode = fingerprint.join("\u0000");
  if (reportedModes.has(mode)) {
    return;
  }
  reportedModes.add(mode);

  const context = { "wf.auth.failure_mode": fingerprint[1] };

  switch (classifyFailure(error)) {
    case "info":
      logger.info(label, context);
      return;
    case "warning":
      // The level, not the message, says a refresh failed transiently, so both call sites keep the
      // label they report an error under. A cause that is not an Error is left out rather than
      // serialised as an opaque blob; `context` carries the failure mode either way.
      addWarning(label, context, error instanceof Error ? error : undefined);
      return;
    case "error":
      // The wrapper is built here rather than by `addError(message, error)` because only the object
      // overload forwards properties to PostHog.
      addError(new Error(label, { cause: error }), {
        ...context,
        $exception_fingerprint: fingerprint,
      });
      return;
  }
}
